import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Scan, ArrowRight, Sparkles, Shield, Zap, Camera, Keyboard, User, LogOut, LayoutDashboard, QrCode, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import QRScanner from '../components/QRScanner';

// SmartCaddy Logo Component - Fresh Theme
const CaddyLogo = ({ size = 'default', showIcon = true }) => {
  const sizes = {
    small: { icon: 'w-8 h-8', text: 'text-xl' },
    default: { icon: 'w-10 h-10', text: 'text-2xl' },
    large: { icon: 'w-14 h-14', text: 'text-4xl' },
    hero: { icon: 'w-20 h-20', text: 'text-6xl' }
  };
  const s = sizes[size];
  
  return (
    <div className="flex items-center gap-3">
      {showIcon && (
        <div className={`${s.icon} rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/30`}>
          <ShoppingCart className="w-1/2 h-1/2 text-white" />
        </div>
      )}
      <span className={`font-caddy ${s.text} lowercase`}>
        <span className="text-caddy-logo">caddy</span>
      </span>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, logout, api } = useAuth();
  const { validateCart, hasActiveSession } = useCart();
  const [cartCode, setCartCode] = useState(searchParams.get('cart') || '');
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);

  // Lookup cart by QR ID (UUID)
  const lookupCartByQrId = async (qrId) => {
    try {
      const response = await api.get(`/carts/qr/${qrId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleValidateCart = async (code, isQrId = false) => {
    if (!code.trim()) {
      toast.error('Please enter a cart code');
      return;
    }

    setLoading(true);
    try {
      let result;
      
      if (isQrId) {
        // UUID-based QR code - lookup by QR ID
        result = await lookupCartByQrId(code);
      } else {
        // Regular cart code validation
        result = await validateCart(code.toUpperCase());
      }
      
      if (result.valid) {
        if (isAuthenticated) {
          navigate(`/shop?cart=${result.cart_id}`);
        } else {
          navigate(`/login?cart=${result.cart_id}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid cart code');
    } finally {
      setLoading(false);
    }
  };

  const handleQRScan = (scannedCode, isQrId = false) => {
    setShowScanner(false);
    setCartCode(isQrId ? `QR:${scannedCode.substring(0, 8)}...` : scannedCode);
    handleValidateCart(scannedCode, isQrId);
  };

  const features = [
    {
      icon: <Scan className="w-6 h-6" />,
      title: 'Scan & Shop',
      description: 'RFID auto-detects items as you add them',
      color: 'bg-teal-100 text-teal-600'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Skip the Queue',
      description: 'Checkout instantly from your phone',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: 'Smart Offers',
      description: 'Personalized deals while you shop',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Secure & Fast',
      description: 'Bank-grade security for payments',
      color: 'bg-sky-100 text-sky-600'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <CaddyLogo size="small" />
          
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                {hasActiveSession && (
                  <Button 
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600" 
                    size="sm" 
                    onClick={() => navigate('/shop')}
                  >
                    <ShoppingCart className="w-4 h-4 mr-1" /> Continue Shopping
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  onClick={() => navigate('/dashboard')}
                >
                  <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                  onClick={logout}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                className="border-teal-500 text-teal-600 hover:bg-teal-500 hover:text-white"
                onClick={() => navigate('/login')}
              >
                <User className="w-4 h-4 mr-1" /> Login
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-teal-100/20 to-orange-100/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <CaddyLogo size="hero" />
              </motion.div>
            </div>

            {/* Tagline */}
            <h1 className="text-3xl md:text-5xl font-bold mb-4 text-slate-800">
              Shop Smarter, <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">Skip the Queue</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-10">
              Grab a caddy, scan the QR code, and let your cart do the counting.
              Checkout from your phone in seconds.
            </p>

            {/* Main CTA Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="max-w-md mx-auto"
            >
              <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
                {/* QR Code visual header */}
                <div className="bg-gradient-to-b from-teal-50 to-transparent p-6 pb-4">
                  <div className="w-24 h-24 mx-auto rounded-2xl border-4 border-teal-500 flex items-center justify-center bg-white shadow-lg">
                    <QrCode className="w-12 h-12 text-teal-500" />
                  </div>
                  <p className="text-slate-600 text-sm mt-4">Scan your caddy&apos;s QR code to start</p>
                </div>

                <CardContent className="p-6 space-y-4">
                  {!showManualInput ? (
                    <>
                      <Button
                        data-testid="scan-qr-btn"
                        onClick={() => setShowScanner(true)}
                        className="w-full h-14 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white text-lg font-semibold rounded-xl shadow-lg shadow-teal-500/25"
                      >
                        <Camera className="w-5 h-5 mr-2" />
                        Scan QR Code
                      </Button>
                      
                      <Button
                        data-testid="manual-code-btn"
                        variant="outline"
                        onClick={() => setShowManualInput(true)}
                        className="w-full h-12 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl"
                      >
                        <Keyboard className="w-4 h-4 mr-2" />
                        Enter Code Manually
                      </Button>
                    </>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-3"
                    >
                      <Input
                        data-testid="cart-code-input"
                        placeholder="Enter caddy code (e.g., CART001)"
                        value={cartCode}
                        onChange={(e) => setCartCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const code = cartCode.trim();
                            // Check if it's a UUID format
                            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(code);
                            handleValidateCart(isUuid ? code.toLowerCase() : code, isUuid);
                          }
                        }}
                        className="h-14 text-center text-lg uppercase tracking-wider bg-slate-50 border-slate-300 text-slate-800 placeholder:text-slate-400 rounded-xl"
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowManualInput(false)}
                          className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl"
                        >
                          Back
                        </Button>
                        <Button
                          data-testid="validate-cart-btn"
                          onClick={() => {
                            const code = cartCode.trim();
                            // Check if it's a UUID format
                            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(code);
                            handleValidateCart(isUuid ? code.toLowerCase() : code, isUuid);
                          }}
                          disabled={loading || !cartCode.trim()}
                          className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl"
                        >
                          {loading ? 'Validating...' : 'Continue'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>

              {/* User login prompt */}
              {!isAuthenticated && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-slate-500 text-sm mt-6"
                >
                  Already have an account?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-teal-600 hover:underline font-medium"
                  >
                    Sign in here
                  </button>
                </motion.p>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">
              Why <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">caddy</span>?
            </h2>
            <p className="text-slate-600">The smartest way to shop in-store</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-white border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all h-full">
                  <CardContent className="p-5 text-center">
                    <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${feature.color}`}>
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">{feature.title}</h3>
                    <p className="text-sm text-slate-500">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">
              How It Works
            </h2>
            <p className="text-slate-600">Three simple steps to faster shopping</p>
          </motion.div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-0">
            {[
              { step: '1', title: 'Grab a Caddy', desc: 'Pick any caddy with a QR code' },
              { step: '2', title: 'Scan & Shop', desc: 'Items auto-add via RFID' },
              { step: '3', title: 'Pay & Go', desc: 'Checkout from your phone' },
            ].map((item, index) => (
              <React.Fragment key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="text-center flex-1 max-w-[200px]"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/30">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </motion.div>
                {index < 2 && (
                  <ChevronRight className="hidden md:block w-8 h-8 text-slate-300 mx-4" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <CaddyLogo size="small" />
          <p className="text-slate-400 text-sm">
            © 2025 SmartCaddy. Powered by SmartCaddy.store
          </p>
          <div className="flex gap-4 text-sm text-slate-500">
            <a href="/admin" className="hover:text-teal-600 transition-colors">Store Admin</a>
          </div>
        </div>
      </footer>

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default LandingPage;
